import React, { useRef, useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { makeStyles } from "@mui/styles";
import { Collapse, IconButton, Slider, Typography } from "@mui/material";
import { PlayArrow, Pause, MicNone, GetApp, ClosedCaption } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    minWidth: 240,
    maxWidth: 320,
  },
  micIcon: {
    color: theme.palette.primary.main,
    fontSize: 28,
    flexShrink: 0,
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 0,
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  slider: {
    flexGrow: 1,
    padding: "8px 0",
    "& .MuiSlider-thumb": {
      width: 12,
      height: 12,
      marginTop: -5,
    },
    "& .MuiSlider-rail": {
      height: 3,
    },
    "& .MuiSlider-track": {
      height: 3,
    },
  },
  timeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  timeText: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  rateButton: {
    fontSize: "0.65rem",
    padding: "0 4px",
    minWidth: 0,
    color: theme.palette.primary.main,
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { textDecoration: "underline" },
  },
  playButton: {
    padding: 4,
    flexShrink: 0,
  },
  downloadButton: {
    padding: 4,
    flexShrink: 0,
  },
  ccButton: {
    padding: 4,
    flexShrink: 0,
  },
  transcription: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    padding: "4px 8px 2px",
    fontStyle: "italic",
    lineHeight: 1.4,
    maxWidth: 300,
    wordBreak: "break-word",
  },
}));

const RATES = [0.5, 1, 1.5, 2];
const LS_NAME = "audioMessageRate";

function formatTime(secs) {
  if (!secs || isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Audio({ url, messageId, fromMe, transcription }) {
  const classes = useStyles();
  const audioRef = useRef(null);
  const markedPlayedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showCC, setShowCC] = useState(false);
  const [rateIdx, setRateIdx] = useState(() => {
    const saved = parseFloat(localStorage.getItem(LS_NAME) || "1");
    return RATES.indexOf(saved) >= 0 ? RATES.indexOf(saved) : 1;
  });

  const rate = RATES[rateIdx];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = rate;
    localStorage.setItem(LS_NAME, rate);
  }, [rate]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrent(audio.currentTime);
    const onLoad = () => setDuration(audio.duration);
    const onEnd = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoad);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoad);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
      if (!fromMe && messageId && !markedPlayedRef.current) {
        markedPlayedRef.current = true;
        api.post(`/messages/${messageId}/mark-played`).catch(() => {});
      }
    }
    setPlaying(!playing);
  }, [playing, fromMe, messageId]);

  const handleSeek = (_, value) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = (value / 100) * duration;
  };

  const cycleRate = () => {
    setRateIdx((i) => (i + 1) % RATES.length);
  };

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div>
      <div className={classes.root}>
        <MicNone className={classes.micIcon} />
        <IconButton className={classes.playButton} size="small" onClick={togglePlay}>
          {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
        </IconButton>
        <div className={classes.controls}>
          <div className={classes.progressRow}>
            <Slider
              className={classes.slider}
              value={progress}
              onChange={handleSeek}
              min={0}
              max={100}
              step={0.1}
            />
          </div>
          <div className={classes.timeRow}>
            <Typography className={classes.timeText}>
              {playing ? formatTime(current) : formatTime(duration)}
            </Typography>
            <span className={classes.rateButton} onClick={cycleRate}>
              {rate}x
            </span>
          </div>
        </div>
        {transcription && (
          <IconButton
            className={classes.ccButton}
            size="small"
            onClick={() => setShowCC(v => !v)}
            title="Transcrição"
            color={showCC ? "primary" : "default"}
          >
            <ClosedCaption fontSize="small" />
          </IconButton>
        )}
        <IconButton
          className={classes.downloadButton}
          size="small"
          component="a"
          href={url}
          download
          target="_blank"
        >
          <GetApp fontSize="small" />
        </IconButton>
        <audio ref={audioRef} src={url} preload="metadata" style={{ display: "none" }} />
      </div>
      {transcription && (
        <Collapse in={showCC}>
          <Typography className={classes.transcription}>{transcription}</Typography>
        </Collapse>
      )}
    </div>
  );
}
